import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { switchOrganization } from '@/modules/auth/services/auth.service';
import { useAuthStore } from '@/state/store/auth';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import { useGetMultiOrgs } from '@/lib/api/hooks/use-multi-orgs';
import {
  excludeDefaultOrganizations,
  isDefaultOrganizationId,
  isOrganizationEnabled,
} from '@/lib/auth/org-access';

const FALLBACK_ORGANIZATION_NAME = 'Ron Orp';

export const useOrganizationSwitcher = () => {
  const [isSwitching, setIsSwitching] = useState(false);
  const { t } = useTranslation();
  const { setTokens, selectedOrgId } = useAuthStore();
  const { toast } = useToast();

  const { data: account, isLoading: isLoadingAccount, isSuccess: isAccountLoaded } = useGetAccount();
  const { data: orgsData, isLoading: isLoadingOrgs } = useGetMultiOrgs({
    enabled: isAccountLoaded && !!account,
  });

  const membershipOrgIds = useMemo(
    () =>
      (account?.memberships?.map((membership) => membership.organizationId) ?? []).filter(
        (id) => !isDefaultOrganizationId(id)
      ),
    [account]
  );

  const availableOrganizations = useMemo(() => {
    const organizations = excludeDefaultOrganizations(
      (orgsData?.organizations ?? []).filter(isOrganizationEnabled)
    );

    if (membershipOrgIds.length === 0) {
      return organizations;
    }

    return organizations.filter((org) => membershipOrgIds.includes(org.itemId));
  }, [membershipOrgIds, orgsData?.organizations]);

  const selectedOrganization = useMemo(() => {
    if (selectedOrgId && !isDefaultOrganizationId(selectedOrgId)) {
      const matchedOrg = availableOrganizations.find((org) => org.itemId === selectedOrgId);
      if (matchedOrg) return matchedOrg;
    }

    return availableOrganizations[0] ?? null;
  }, [availableOrganizations, selectedOrgId]);

  const currentOrgRoles = useMemo(() => {
    if (!account?.memberships?.length || !selectedOrgId || isDefaultOrganizationId(selectedOrgId)) {
      return [];
    }
    const membership = account.memberships.find((m) => m.organizationId === selectedOrgId);
    return membership?.roles ?? [];
  }, [account, selectedOrgId]);

  const handleOrganizationSelect = async (orgId: string, onSuccess?: () => void) => {
    if (isSwitching || orgId === selectedOrgId || isDefaultOrganizationId(orgId)) {
      return;
    }

    try {
      setIsSwitching(true);

      const response = await switchOrganization(orgId);

      setTokens({
        accessToken: response.access_token,
        refreshToken: (response.refresh_token || useAuthStore.getState().refreshToken) ?? '',
      });

      onSuccess?.();
      window.location.reload();
      return;
    } catch (error) {
      let errorTitle = t('FAILED_TO_SWITCH_ORGANIZATION');
      let errorDescription = t('SOMETHING_WENT_WRONG');

      if (error instanceof HttpError) {
        const errorData = error.error;

        if (errorData?.error === 'user_inactive_or_not_verified') {
          errorTitle = t('ACCESS_DENIED');
          errorDescription =
            typeof errorData?.error_description === 'string'
              ? errorData.error_description
              : t('USER_NOT_EXIST_IN_ORGANIZATION');
        } else if (typeof errorData?.error_description === 'string') {
          errorDescription = errorData.error_description;
        } else if (typeof errorData?.error === 'string') {
          errorDescription = errorData.error;
        }
      }

      toast({
        variant: 'destructive',
        title: errorTitle,
        description: errorDescription,
      });
    } finally {
      setIsSwitching(false);
    }
  };

  return {
    account,
    availableOrganizations,
    currentOrgRoles,
    handleOrganizationSelect,
    isLoading: isLoadingAccount || isLoadingOrgs,
    isSwitching,
    selectedOrgId,
    selectedOrganization,
    displayOrgName: selectedOrganization?.name ?? FALLBACK_ORGANIZATION_NAME,
  };
};
